import React, { useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';

const SEARCH_DEBOUNCE_MS = 400;

const VendorFilters = ({ filters, onFiltersChange, onClearFilters }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchText, setSearchText] = useState(filters.search || '');
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const searchDraftRef = useRef(filters.search || '');

  const vendorTypeOptions = [
    { value: '', label: 'All Types' },
    // Values must match registration (step 4) / DB: manufacturing, trading, service
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'trading', label: 'Trading' },
    { value: 'service', label: 'Service' },
  ];

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    { value: 'small', label: 'small' },
    { value: 'medium', label: 'medium' },
    { value: 'micro', label: 'micro' },
    { value: 'N/A', label: 'N/A' },
  ];

  const countryOptions = [
    { value: '', label: 'All Countries' },
    { value: 'IN', label: 'India' },
    { value: 'US', label: 'United States' },
    { value: 'CN', label: 'China' },
    { value: 'DE', label: 'Germany' },
    { value: 'JP', label: 'Japan' },
    { value: 'GB', label: 'United Kingdom' }
  ];

  const msmeStatusOptions = [
    { value: '', label: 'All MSME Status' },
    { value: 'msme', label: 'MSME' },
    { value: 'non_msme', label: 'Non-MSME' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' }
  ];

  const debounceTimeoutRef = useRef(null);

  const handleClearAll = () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    setSearchText('');
    searchDraftRef.current = '';
    onClearFilters();
  };

  const handleFilterChange = (key, value) => {
    if (key === 'search') {
      setSearchText(value);
      searchDraftRef.current = value;
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => {
        debounceTimeoutRef.current = null;
        onFiltersChange({
          ...filtersRef.current,
          search: searchDraftRef.current,
        });
      }, SEARCH_DEBOUNCE_MS);
      return;
    }
    onFiltersChange({ ...filtersRef.current, [key]: value });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => {
      if (!value || value === '') return false;
      return true;
    }).length;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Card className="border border-slate-200 shadow-sm bg-white">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Icon name="Filter" size={20} className="text-slate-700" />
            </div>
            <CardTitle className="text-lg font-semibold text-slate-900">Filters</CardTitle>
            {activeFiltersCount > 0 && (
              <Badge variant="default" className="ml-2">
                {activeFiltersCount}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeFiltersCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearAll}
                iconName="X"
                iconPosition="left"
              >
                Clear All
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              iconPosition="right"
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">

        {/* Quick Filters - Always Visible */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Input
          type="search"
          placeholder="Search vendors..."
          value={searchText}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          className="w-full"
        />
        
        <Select
          placeholder="Select status"
          options={statusOptions}
          value={filters.status || ''}
          onChange={(value) => handleFilterChange('status', value)}
        />

        <Select
          placeholder="Select type"
          options={vendorTypeOptions}
          value={filters.vendorType || ''}
          onChange={(value) => handleFilterChange('vendorType', value)}
        />

        <Select
          placeholder="Select country"
          options={countryOptions}
          value={filters.country || ''}
          onChange={(value) => handleFilterChange('country', value)}
          searchable
        />
      </div>

        {/* Advanced Filters - Expandable */}
        {isExpanded && (
          <div className="border-t border-slate-200 pt-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <Select
              label="Category"
              placeholder="Select category"
              options={categoryOptions}
              value={filters.category || ''}
              onChange={(value) => handleFilterChange('category', value)}
            />

            <Select
              label="MSME Status"
              placeholder="Select MSME status"
              options={msmeStatusOptions}
              value={filters.msmeStatus || ''}
              onChange={(value) => handleFilterChange('msmeStatus', value)}
            />

            <Input
              label="Vendor Code"
              type="text"
              placeholder="Enter vendor code"
              value={filters.vendorCode || ''}
              onChange={(e) => handleFilterChange('vendorCode', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Registration Date Range
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  placeholder="From date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                />
                <Input
                  type="date"
                  placeholder="To date"
                  value={filters.dateTo || ''}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Annual Turnover Range (₹)
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="number"
                  placeholder="Min amount"
                  value={filters.turnoverMin || ''}
                  onChange={(e) => handleFilterChange('turnoverMin', e.target.value)}
                />
                <Input
                  type="number"
                  placeholder="Max amount"
                  value={filters.turnoverMax || ''}
                  onChange={(e) => handleFilterChange('turnoverMax', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Active Filter Chips */}
        {activeFiltersCount > 0 && (
          <div className="border-t border-slate-200 pt-4 mt-4">
            <div className="flex flex-wrap gap-2">
              {Object.entries(filters).map(([key, value]) => {
                if (!value || value === '') return null;
                
                const getFilterLabel = (key, value) => {
                  switch (key) {
                    case 'search': return `Search: "${value}"`;
                    case 'status': return `Status: ${statusOptions.find(opt => opt.value === value)?.label || value}`;
                    case 'vendorType': return `Type: ${vendorTypeOptions.find(opt => opt.value === value)?.label || value}`;
                    case 'country': return `Country: ${countryOptions.find(opt => opt.value === value)?.label || value}`;
                    case 'category': return `Category: ${categoryOptions.find(opt => opt.value === value)?.label || value}`;
                    case 'msmeStatus': return `MSME: ${msmeStatusOptions.find(opt => opt.value === value)?.label || value}`;
                    case 'vendorCode': return `Code: ${value}`;
                    case 'dateFrom': return `From: ${value}`;
                    case 'dateTo': return `To: ${value}`;
                    case 'turnoverMin': return `Min: ₹${value}`;
                    case 'turnoverMax': return `Max: ₹${value}`;
                    default: return `${key}: ${value}`;
                  }
                };

                return (
                  <Badge
                    key={key}
                    variant="secondary"
                    className="flex items-center gap-1.5 pr-1"
                  >
                    <span>{getFilterLabel(key, value)}</span>
                    <button
                      onClick={() => handleFilterChange(key, '')}
                      className="hover:bg-slate-200 rounded-full p-0.5 transition-all ml-1"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </Badge>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VendorFilters;